"use client";

import React, { useMemo } from "react";
import FullPageContainer from "@/components/FullPageCom/FullPageContainer";
import FullPageSection from "@/components/FullPageCom/FullPageSection";
import HeroSection from "@/components/Homepage/HeroSection/HeroSection";
import VideoSection from "@/components/Homepage/VideoSection/VideoSection";
import ProductSpotlightSection from "@/components/Homepage/ProductsSection/ProductSpotlightSection";
import PartnerSection from "@/components/Homepage/PartnerSection/PartnerSection";
import NewsSection from "@/components/Homepage/NewsSection/NewsSection";
import Footer from "@/components/Layouts/Footer";
import { stripHTML } from "@/utils/stripHTML";
import { parseProjectInfo } from "@/context/ProjectInfoContext";

interface HomeProps {
  seo: any;
  data: any;
  locale: string;
}

interface SectionConfig {
  id: string;
  label: string;
  index: number;
  shared: boolean;
  transparent?: boolean;
  hideDotNav?: boolean;
  noCenter?: boolean;
  render: (props: { index: number; isActive: boolean }) => React.ReactNode;
}

export default function HomeClient({ data, seo, locale }: HomeProps) {
  // #region Helper Functions (CMS Data Parsing & Section Control)

  // Gom tất cả các mảng add_on_data từ CMS để tìm kiếm linh hoạt
  const allAddOn1 = useMemo(() => {
    const list1 = Array.isArray(data?.add_on_data) ? data.add_on_data : [];
    return list1;
  }, [data]);
  const allAddOn2 = useMemo(() => {
    const list2 = Array.isArray(data?.add_on_data_2) ? data.add_on_data_2 : [];
    return list2;
  }, [data]);
  const allAddOn3 = useMemo(() => {
    const list3 = Array.isArray(data?.add_on_data_3) ? data.add_on_data_3 : [];
    return list3;
  }, [data]);

  const getAddOn1 = (keys: string[]) => {
    const lowerKeys = keys.map((k) => k.toLowerCase());
    return (
      allAddOn1.find((item: any) => {
        const title = (item?.title || "").toLowerCase();
        const summary = (item?.summary || "").toLowerCase();
        return lowerKeys.some(
          (k) => title === k || summary === k || title.includes(k)
        );
      }) || null
    );
  };

  const isSectionShared = (keys: string[], defaultVal = true): boolean => {
    const item = getAddOn1(keys);
    if (!item) return defaultVal;
    return (
      item.shared !== false &&
      item.shared !== "false" &&
      item.shared !== 0 &&
      item.shared !== "0"
    );
  };

  const getSectionIndex = (keys: string[], defaultVal: number): number => {
    const addon = getAddOn1(keys);
    if (!addon) return defaultVal;
    const stt = addon.stt;
    if (stt === undefined || stt === null || stt === "") {
      return defaultVal;
    }
    const val = Number(stt);
    return isNaN(val) ? defaultVal : val;
  };
  // #endregion

  //#region hero data fetch
  const heroData = getAddOn1(["hero"]);
  const heroBanners = useMemo(() => {
    if (!heroData) return [];
    return [
      heroData.picture,
      heroData.picture_2,
      heroData.picture_3,
      heroData.picture_4,
      heroData.picture_5,
      heroData.picture_6,
    ].filter(Boolean);
  }, [heroData]);
  //#endregion

  //#region video
  const videoData = getAddOn1(["video"]);

  // Lấy link video và thêm tham số autoplay, tắt tiếng (mute=1) để chạy mượt và ẩn nút
  let linkVideo = videoData?.content_3 || videoData?.summary || "";
  if (linkVideo && !linkVideo.includes("autoplay=1")) {
    linkVideo +=
      (linkVideo.includes("?") ? "&" : "?") +
      "autoplay=1&mute=0&controls=0&rel=0&modestbranding=1&iv_load_policy=3";
  }

  // Ảnh trang trí từ picture và picture_2
  const decoImage1 = videoData?.picture || "";
  const decoImage2 = videoData?.picture_2 || "";
  //#endregion

  //#region stat
  let stat: any[] = [];
  try {
    const rawContent = videoData?.content || "";

    const cleanContent = stripHTML(rawContent).trim();

    if (cleanContent) {
      stat = JSON.parse(cleanContent);
    }
  } catch (error) {
    console.error("Error parsing video stat JSON:", error);
    stat = [];
  }
  //#endregion

  //#region products data fetch & parse
  const productAddOns = useMemo(() => {
    // Lấy tất cả các record có title là "san-pham"
    return allAddOn2.filter(
      (item: any) =>
        (item?.title || "").toLowerCase() === "san-pham" &&
        item?.shared !== false &&
        item?.shared !== "false"
    );
  }, [allAddOn2]);

  const parsedProducts = useMemo(() => {
    return productAddOns.map((item: any, idx: number) => ({
      id: item._id || item.id || item.__id_tmp__ || item.stt || idx,
      title: item.summary || item.title || "",
      description: item.content?.replace(/<[^>]*>/g, "") || "",
      link: item.content_3 || item.summary || "",
      color: item.content_2 || item.content_3 || "#008748",
      mainImage1: item.picture || "",
      mainImage2: item.picture_2 || "",
      vec: item.picture_3 || "",
    }));
  }, [productAddOns]);
  //#endregion

  //#region Partner
  const partnerData = getAddOn1(["doi-tac-chung-nhan"]);

  // Parse headers JSON từ content của record doi-tac-chung-nhan
  const partnerHeaders = useMemo(() => {
    let parsed: any = null;
    try {
      const rawContent = partnerData?.content || "";
      const cleanContent = stripHTML(rawContent).trim();
      if (cleanContent) {
        parsed = JSON.parse(cleanContent);
      }
    } catch (error) {
      console.error("Error parsing partner headers JSON:", error);
    }
    return {
      partners: parsed?.partners || {
        title: locale === "vi" ? "ĐỐI TÁC CỦA CHÚNG TÔI" : "OUR PARTNERS",
        desc:
          locale === "vi"
            ? "Chúng tôi tự hào hợp tác cùng các thương hiệu uy tín hàng đầu Việt Nam và quốc tế."
            : "We are proud to partner with leading reputable brands in Vietnam and internationally.",
      },
      certifications: parsed?.certifications || {
        title:
          locale === "vi" ? "CHỨNG NHẬN CHẤT LƯỢNG" : "QUALITY CERTIFICATION",
        desc:
          locale === "vi"
            ? "Các chứng nhận chất lượng sản phẩm, đảm bảo uy tín và niềm tin cho đối tác."
            : "Product quality certifications, ensuring prestige and trust for partners.",
      },
    };
  }, [partnerData, locale]);

  // Lọc logos và certifications từ allAddOn2
  const partnerLogos = useMemo(() => {
    return allAddOn2
      .filter((item: any) => (item?.title || "").toLowerCase() === "doi-tac")
      .flatMap((item: any) => [
        item.picture,
        item.picture_2,
        item.picture_3,
        item.picture_4,
        item.picture_5,
        item.picture_6,
      ])
      .filter(Boolean);
  }, [allAddOn2]);

  const certificationImages = useMemo(() => {
    return allAddOn2
      .filter((item: any) => (item?.title || "").toLowerCase() === "chung-nhan")
      .flatMap((item: any) => [
        item.picture,
        item.picture_2,
        item.picture_3,
        item.picture_4,
        item.picture_5,
        item.picture_6,
      ])
      .filter(Boolean);
  }, [allAddOn2]);

  //#endregion

  //#region News
  const newsData = getAddOn1(["tin-tuc"]);
  const newsHeader = useMemo(() => {
    return {
      title:
        newsData?.summary ||
        (locale === "vi" ? "TIN TỨC MỚI NHẤT" : "LATEST NEWS"),
      subtitle:
        newsData?.content_3 ||
        (locale === "vi"
          ? "Cập nhật thông tin nổi bật mới nhất"
          : "Stay updated with the latest news"),
      decoImage: newsData?.picture || "",
    };
  }, [newsData, locale]);
  //#endregion

  //#region Footer
  const footerData = useMemo(() => {
    return parseProjectInfo(data?.content);
  }, [data?.content]);
  //#endregion

  //#region Danh sách các section và logic cấu hình
  const sections = useMemo(() => {
    const list: SectionConfig[] = [];

    // 1. HERO SECTION (Banner đầu trang)
    if (isSectionShared(["hero"], true)) {
      list.push({
        id: "hero",
        label: locale === "vi" ? "Trang chủ" : "Home",
        index: getSectionIndex(["hero"], 1),
        shared: true,
        transparent: true,
        hideDotNav: true,
        render: () => <HeroSection banners={heroBanners} />,
      });
    }

    // 2. VIDEO SECTION (Giới thiệu / Video)
    if (isSectionShared(["video"], true)) {
      list.push({
        id: "video",
        label: locale === "vi" ? "Video" : "Video",
        index: getSectionIndex(["video"], 2),
        shared: true,
        render: ({ isActive }) => (
          <VideoSection
            stat={stat}
            isActive={isActive}
            propLinkVideo={linkVideo}
            decoImage1={decoImage1}
            decoImage2={decoImage2}
            locale={locale}
          />
        ),
      });
    }

    // 3. PRODUCTS SECTION (Sản phẩm nổi bật)
    if (isSectionShared(["san-pham"], true)) {
      list.push({
        id: "products",
        label: locale === "vi" ? "Sản phẩm" : "Products",
        index: getSectionIndex(["san-pham"], 3),
        shared: true,
        render: () => (
          <ProductSpotlightSection
            products={parsedProducts}
            data={data}
            locale={locale}
          />
        ),
      });
    }

    // 4. PARTNER SECTION (Đối tác & Chứng nhận)
    if (isSectionShared(["doi-tac-chung-nhan"], true)) {
      list.push({
        id: "partners",
        label: locale === "vi" ? "Đối tác & chứng chỉ" : "Partners & Certificates",
        index: getSectionIndex(["doi-tac-chung-nhan"], 4),
        shared: true,
        render: ({ isActive }) => (
          <PartnerSection
            isActive={isActive}
            partnerLogos={partnerLogos}
            certificationImages={certificationImages}
            partnerHeader={partnerHeaders.partners}
            certHeader={partnerHeaders.certifications}
            locale={locale}
          />
        ),
      });
    }

    // 5. NEWS SECTION (Tin tức & Sự kiện)
    if (isSectionShared(["tin-tuc"], true)) {
      list.push({
        id: "news",
        label: locale === "vi" ? "Tin tức & Sự kiện" : "News & Events",
        index: getSectionIndex(["tin-tuc"], 5),
        shared: true,
        render: ({ isActive }) => (
          <NewsSection
            isActive={isActive}
            locale={locale}
            title={newsHeader.title}
            subtitle={newsHeader.subtitle}
            decoImage={newsHeader.decoImage}
          />
        ),
      });
    }

    // 6. FOOTER SECTION (Chân trang / Liên hệ)
    if (isSectionShared(["footer"], true)) {
      list.push({
        id: "contact",
        label: locale === "vi" ? "Liên hệ" : "Contact",
        index: getSectionIndex(["footer"], 999),
        shared: true,
        hideDotNav: true,
        render: () => <Footer forceStatic={true} data={footerData} />,
      });
    }

    // Sắp xếp thứ tự section theo thuộc tính index (stt trên CMS)
    return list.sort((a, b) => a.index - b.index);
  }, [
    allAddOn1,
    data,
    locale,
    heroBanners,
    linkVideo,
    decoImage1,
    decoImage2,
    stat,
    parsedProducts,
    partnerHeaders,
    partnerLogos,
    certificationImages,
    newsHeader,
    footerData,
  ]);
  //#endregion;
  return (
    <main className="min-h-screen bg-white">
      <FullPageContainer>
        {sections.map((section, actualIndex) => (
          <FullPageSection
            key={section.id}
            id={section.id}
            label={section.label}
            index={actualIndex}
            transparent={section.transparent}
            hideDotNav={section.hideDotNav}
            noCenter={section.noCenter}
          >
            {({ isActive }) => section.render({ index: actualIndex, isActive })}
          </FullPageSection>
        ))}
      </FullPageContainer>
    </main>
  );
}
